import { jsPDF } from 'jspdf'
import autoTable from 'jspdf-autotable'
import logoPanda from '@/assets/logo-pandavideo.png'

function sanitizeTitleForPdf(str) {
  if (typeof str !== 'string') return ''
  const allowedCharsOnly = str.replace(/[^\p{L}\p{N}\s.,_%$-]/gu, '')
  return allowedCharsOnly.replace(/\s+/g, ' ').trim()
}

export const generatePdfBlob = async ({
  chartHeaderData,
  chartInstance,
  tableHeaders,
  tableBody,
  htmlImageData = null,
  columnStyles,
  customCellStyler,
}) => {
  const doc = new jsPDF('p', 'mm', 'a4')
  const pageWidth = doc.internal.pageSize.getWidth()

  
  const contentMargin = 12
  const headerMargin = 7
  
  let lastY = headerMargin
  const title = 'Analytics' 
  const logoSrc = logoPanda  

  doc.setFontSize(14)
  doc.setTextColor(40)
  
  const logoWidth = 20
  const logoHeight = 4.6
  
  doc.text(title, headerMargin, lastY + (logoHeight / 2) + 2.5)

  if (logoSrc) {
    const logoX = pageWidth - headerMargin - logoWidth
    doc.addImage(logoSrc, 'PNG', logoX, lastY, logoWidth, logoHeight)
  }
  
  lastY += logoHeight + 15

if (chartHeaderData) {
    const { title, description, metrics } = chartHeaderData    
    doc.setFontSize(12)
    doc.setFont(undefined, 'bold')
    doc.setTextColor(20, 20, 20)
    doc.text(title, contentMargin, lastY)
    lastY += 6

    doc.setFontSize(9)
    doc.setFont(undefined, 'normal')
    doc.setTextColor(100, 100, 100)
    doc.text(description, contentMargin, lastY)
    lastY += 10

    if (metrics && metrics.length > 0) {
      let currentX = contentMargin
      metrics.forEach(metric => {
        const rgb = metric.color.replace('#', '').match(/.{1,2}/g).map(x => parseInt(x, 16))
        doc.setFillColor(rgb[0], rgb[1], rgb[2])
        doc.circle(currentX + 1.5, lastY - 1, 1.5, 'F')
        doc.setFontSize(7)
        doc.setFont(undefined, 'normal')
        doc.setTextColor(20, 20, 20)
        const metricText = `${metric.label} ${metric.value}`
        doc.text(metricText, currentX + 5, lastY)
        currentX += doc.getTextWidth(metricText) + 10
      })
    }
    
    lastY += 8
  }

  if (htmlImageData) {
    doc.addImage(
      htmlImageData.uri,
      'PNG',
      contentMargin,
      lastY, 
      htmlImageData.width,
      htmlImageData.height
    )
    lastY += htmlImageData.height + 15 
  
  } else if (chartInstance) {
    const { imgURI } = await chartInstance.dataURI({
      width: 4000,
    })
    const chartImgWidth = pageWidth - contentMargin * 2
    const chartImgHeight = (chartInstance.w.globals.dom.Paper.node.clientHeight * chartImgWidth) / chartInstance.w.globals.dom.Paper.node.clientWidth
    doc.addImage(imgURI, 'PNG', contentMargin, lastY, chartImgWidth, chartImgHeight)
    lastY += chartImgHeight + 15
  }


  autoTable(doc, {
    head: [tableHeaders],
    body: tableBody,
    startY: lastY,
    margin: { left: contentMargin, right: contentMargin },
    theme: 'plain',
    styles: { fontSize: 8, cellPadding: 2.5, textColor: '#212121' },
    headStyles: { fontStyle: 'bold', textColor: '#212121' },
    columnStyles: columnStyles, 
    didParseCell: function (data) {
      if (data.section === 'body') {
        const originalText = data.cell.text[0] || ''
        const sanitizedText = sanitizeTitleForPdf(originalText)
        data.cell.text[0] = sanitizedText
      }
      if (typeof customCellStyler === 'function') {
        customCellStyler(data)
      }
    },
    willDrawCell: (data) => {
      if (data.column.index > 0) {
        data.cell.styles.halign = 'right'
      }
    },
    didDrawCell: (data) => {
      if (data.section === 'head' || data.section === 'body') {
        doc.setDrawColor(221, 221, 221)
        doc.setLineWidth(0.2)
        doc.line(data.cell.x, data.cell.y + data.cell.height, data.cell.x + data.cell.width, data.cell.y + data.cell.height)
      }
    },
  })
  return doc.output('blob')
}

export const generatePdfComparerBlob = async ({
  chartHeaderData,
  chartInstance,
  tableHeaders,
  tableBody,
  htmlImageData = null,
  columnStyles,
  customCellStyler,
  capturedChartImages,
  hasTwoVideos,
}) => {
  const doc = new jsPDF('p', 'mm', 'a4')
  const pageWidth = doc.internal.pageSize.getWidth()

  
  const contentMargin = 12
  const headerMargin = 7
  
  let lastY = headerMargin
  const title = 'Analytics' 
  const logoSrc = logoPanda  

  doc.setFontSize(14)
  doc.setTextColor(40)
  
  const logoWidth = 20
  const logoHeight = 4.6
  
  doc.text(title, headerMargin, lastY + (logoHeight / 2) + 2.5)

  if (logoSrc) {
    const logoX = pageWidth - headerMargin - logoWidth
    doc.addImage(logoSrc, 'PNG', logoX, lastY, logoWidth, logoHeight)
  }
  
  lastY += logoHeight + 15

if (chartHeaderData) {
    const { title, description, metrics } = chartHeaderData    
    doc.setFontSize(12)
    doc.setFont(undefined, 'bold')
    doc.setTextColor(20, 20, 20)
    doc.text(title, contentMargin, lastY)
    lastY += 6

    doc.setFontSize(9)
    doc.setFont(undefined, 'normal')
    doc.setTextColor(100, 100, 100)
    doc.text(description, contentMargin, lastY)
    lastY += 10

    if (metrics && metrics.length > 0) {
      let currentX = contentMargin
      metrics.forEach(metric => {
        const rgb = metric.color.replace('#', '').match(/.{1,2}/g).map(x => parseInt(x, 16))
        doc.setFillColor(rgb[0], rgb[1], rgb[2])
        doc.circle(currentX + 1.5, lastY - 1, 1.5, 'F')
        doc.setFontSize(7)
        doc.setFont(undefined, 'normal')
        doc.setTextColor(20, 20, 20)
        const metricText = `${metric.label} ${metric.value}`
        doc.text(metricText, currentX + 5, lastY)
        currentX += doc.getTextWidth(metricText) + 10
      })
    }
    
    lastY += 8
  }

  if (htmlImageData) {
    doc.addImage(
      htmlImageData.uri,
      'PNG',
      contentMargin,
      lastY, 
      htmlImageData.width,
      htmlImageData.height
    )
    lastY += htmlImageData.height + 15 
  
  } else if (chartInstance) {
    const { imgURI } = await chartInstance.dataURI({
      width: 4000,
    })
    const chartImgWidth = pageWidth - contentMargin * 2
    const chartImgHeight = (chartInstance.w.globals.dom.Paper.node.clientHeight * chartImgWidth) / chartInstance.w.globals.dom.Paper.node.clientWidth
    doc.addImage(imgURI, 'PNG', contentMargin, lastY, chartImgWidth, chartImgHeight)
    lastY += chartImgHeight + 15
  }

  autoTable(doc, {
    head: [tableHeaders],
    body: tableBody,
    startY: lastY,
    margin: { left: contentMargin, right: contentMargin },
    theme: 'plain',
    styles: { fontSize: 8, cellPadding: 2.5, textColor: '#212121' },
    headStyles: { fontStyle: 'bold', textColor: '#212121' },
    columnStyles: columnStyles, 
    willDrawCell: (data) => {
      if (data.column.index > 0) {
          data.cell.styles.halign = 'right'
      }
      if (data.section === 'body' && data.column.index === 0) {
          if (data.row.index === 5 && capturedChartImages[0]) {
              data.row.chart = capturedChartImages[0]
              data.row.height = 60
          }
          
          else if (data.row.index === 6 && capturedChartImages[1]) {
              data.row.chart = capturedChartImages[1]
              data.row.height = 60
          }
      }
    },

    didDrawCell: (data) => {
      if (!data.row.chart) {
        doc.setDrawColor(221, 221, 221)
        doc.setLineWidth(0.2)
        doc.line(data.cell.x, data.cell.y + data.cell.height, data.cell.x + data.cell.width, data.cell.y + data.cell.height)
    }

        if (data.row.chart && data.column.index === 0) {
            const chart = data.row.chart
            const imageWidth = 160
            const imageHeight = 50
            const imageMarginTop = 15
            let imageX = pageWidth - contentMargin - imageWidth
            if (hasTwoVideos) {
              imageX += 60 
          }
            
            doc.addImage(
                chart.imageData,
                'PNG',
                imageX,
                data.cell.y + imageMarginTop,
                imageWidth,
                imageHeight,
            )
        }
    },
    didParseCell: function (data) {
      if (data.section === 'body') {
        const originalText = data.cell.text[0] || ''
        const sanitizedText = sanitizeTitleForPdf(originalText)
        data.cell.text[0] = sanitizedText
      }
      if (typeof customCellStyler === 'function') {
        customCellStyler(data)
      }
    }
  })

  return doc.output('blob')
}

export const downloadBlob = (blob, filename) => {
  const url = window.URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.style.display = 'none'
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  window.URL.revokeObjectURL(url)
  document.body.removeChild(a)
}